import type { DesignBrief } from "@/types/createAgent";
import { normalize } from "./briefText";

const CONSTRAINT_GRAMMAR_WORDS = new Set([
  "a",
  "an",
  "and",
  "avoid",
  "do",
  "dont",
  "have",
  "just",
  "keep",
  "must",
  "no",
  "not",
  "only",
  "simply",
  "the",
  "without",
]);

function canonicalConstraintWord(word: string): string {
  // Catalog values use both "neck" and "neckline" for the same feature.
  // Canonicalizing the noun lets a positive V-neck choice clear a stale
  // avoidance phrased as "V neckline" (including the formerly-corrupted
  // "just a V neckline" value).
  return /^necklines?$/.test(word) ? "neck" : word;
}

function constraintWords(value: string): string[] {
  return normalize(value)
    .split(/\s+/)
    .filter(Boolean)
    .filter(word => !CONSTRAINT_GRAMMAR_WORDS.has(word))
    .map(canonicalConstraintWord);
}

/**
 * A removal is directional: removing broad "lace" clears stored "black lace",
 * while removing scoped "black lace" does not erase a broader "lace"
 * requirement. That same directionality is important for conflict detection.
 */
function storedEntryMatchesRemoval(stored: string, removal: string): boolean {
  const storedWords = new Set(constraintWords(stored));
  const removalWords = constraintWords(removal);
  if (storedWords.size === 0 || removalWords.length === 0) return false;
  return removalWords.every(word => storedWords.has(word));
}

export function withoutRemovedEntries(
  entries: string[],
  removals: string[]
): string[] {
  return entries.filter(
    entry =>
      !removals.some(removal => storedEntryMatchesRemoval(entry, removal))
  );
}

export function withoutExactEntries(
  entries: string[],
  removals: string[]
): string[] {
  const normalizedRemovals = new Set(removals.map(normalize).filter(Boolean));
  return entries.filter(entry => !normalizedRemovals.has(normalize(entry)));
}

// True when `value` satisfies rejection `entry`: every significant word of the
// entry appears as a WHOLE WORD in the value. Whole-word (not substring)
// matching is essential — a raw includes() makes rejected "heavy" match the
// fabric descriptor "Heavyweight satin", and makes the compound rejection
// "black lace" match a bare "Black" color. This one predicate powers both
// enforcement (suppress a rejected option) and reversal (an explicit pick of
// the value clears that rejection).
export function valueMatchesRejection(value: string, entry: string): boolean {
  const valueWords = new Set(constraintWords(value));
  if (valueWords.size === 0) return false;
  const entryWords = constraintWords(entry).filter(
    word => word.length > 2 || word === "v"
  );
  if (entryWords.length === 0) return false;
  return entryWords.every(word => valueWords.has(word));
}

// Removes any rejected-list entry the newly-committed value reverses, so an
// explicit user choice ("actually, black is fine") clears a prior rejection
// instead of being silently vetoed or re-flagged forever. Matches whole words
// so committing "Duchess Satin - Heavyweight..." does not wipe an unrelated
// "heavy" rejection, and picking a bare color does not wipe a compound
// rejection like "black lace".
export function clearReversedRejections(
  brief: DesignBrief,
  value: string
): void {
  const normValue = normalize(value);
  if (!normValue) return;
  const reversed = (entry: string) => valueMatchesRejection(value, entry);
  brief.constraints.avoid = brief.constraints.avoid.filter(
    entry => !reversed(entry)
  );
  brief.rejectedIdeas = brief.rejectedIdeas.filter(entry => !reversed(entry));
  brief.constraints.color.rejected = brief.constraints.color.rejected.filter(
    entry => !reversed(entry)
  );
}
